import { IconName, MemreBoxSelection, MemreButton, MemreFlexBox, MemreInput, MemreText } from 'components/core';
import React, { useEffect, useState } from 'react';

import authoringGoogleDocs from 'assets/images/authoring/google_doc.gif';
import authoringGoogleSlides from 'assets/images/authoring/google_slides.gif';
import authoringPowerpoint from 'assets/images/authoring/powerpoint.gif';
import authoringSlideshare from 'assets/images/authoring/slide-share.gif';
import authoringVimeo from 'assets/images/authoring/vimeo.gif';
import authoringYoutube from 'assets/images/authoring/youtube.gif';

export default function EmbedBlock({ item }) {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [content, setContent] = useState<string | null>(item.url);

  const cardTypes = [
    {
      type: 'Google Docs',
      icon: IconName.document,
      image: authoringGoogleDocs,
      link: 'https://docs.google.com/',
      linkText: 'Open the Google Doc',
      steps: [
        'Open the Google Doc you want to embed',
        'Click "File" in the top left',
        'Click "Share" in the dropdown menu',
        'Click "Publish to web" in the popup',
        'Click the "Embed" tab',
        'Click "Publish"',
        'Click "Copy" to copy the embed code',
        'Paste into the box below',
      ],
    },
    {
      type: 'Google Slides',
      icon: IconName.desktop,
      image: authoringGoogleSlides,
      link: 'https://slides.google.com/',
      linkText: 'Open the Google Slides presentation',
      steps: [
        'Open the Google Slides presentation you want to embed',
        'Click "File" in the top left',
        'Click "Publish to the web" in the dropdown menu',
        'Click the "Embed" tab in the popup',
        'Click "Publish"',
        'Click "Copy" to copy the embed code',
        'Paste into the box below',
      ],
    },
    {
      type: 'Powerpoint',
      icon: IconName.powerpoint,
      image: authoringPowerpoint,
      link: 'https://www.microsoft365.com/launch/powerpoint',
      linkText: 'Open the powerpoint presentation',
      steps: [
        'Open the powerpoint presentation you want to embed',
        'Click "File" in the top left',
        'Hover over "Share" in the popup menu',
        'Click "Embed this presentation"',
        'This will open a new popup',
        'Click "Copy" in the bottom right of the new popup',
        'Paste into the box below',
      ],
    },
    {
      type: 'Prezi',
      icon: IconName.desktop,
      image: authoringPowerpoint, // Using PowerPoint image as a placeholder, you can replace with proper Prezi image
      link: 'https://prezi.com/',
      linkText: 'Open the Prezi presentation',
      steps: [
        'Open the Prezi presentation you want to embed',
        'Click the "Share" button in the top right',
        'Click the "</> Embed" tab in the popup',
        'Copy the embed code',
        'Paste into the box below',
      ],
    },
    {
      type: 'Slideshare',
      icon: IconName.slideshare,
      image: authoringSlideshare,
      link: 'https://www.slideshare.net/',
      linkText: 'Open the Slideshare presentation',
      steps: [
        'Open the Slideshare presentation you want to embed',
        'Click the "Share" button below the presentation',
        'Click "Embed" in the popup',
        'Copy the embed code',
        'Paste into the box below',
      ],
    },
    {
      type: 'Vimeo',
      icon: IconName.vimeo,
      image: authoringVimeo,
      link: 'https://vimeo.com/',
      linkText: 'Open the Vimeo video',
      steps: [
        'Open the Vimeo video you want to embed',
        'Click the "Share" button below the video',
        'Click the "Embed" tab in the popup',
        'Customize the embed options if needed',
        'Copy the embed code',
        'Paste into the box below',
      ],
    },
    {
      type: 'Youtube',
      icon: IconName.youtube,
      image: authoringYoutube,
      link: 'https://www.youtube.com/',
      linkText: 'Open the YouTube video',
      steps: [
        'Open the YouTube video you want to embed',
        'Click the "Share" button below the video',
        'Click "Embed" in the popup menu',
        'Customize the embed options if needed',
        'Click "Copy" to copy the embed code',
        'Paste into the box below',
      ],
    },
  ];

  const handleCardClick = (type: string) => {
    setSelectedType(type);
  };

  const handleEmbedClick = async () => {
    const clipboard = await navigator.clipboard.readText();
    setContent(clipboard);
  };

  useEffect(() => {
    if (content) {
      const widthMatch = content.match(/width="(\d+)(?:px)?"/);
      const heightMatch = content.match(/height="(\d+)(?:px)?"/);

      if (widthMatch && widthMatch[1] && heightMatch && heightMatch[1]) {
        const originalWidth = parseInt(widthMatch[1], 10);
        const originalHeight = parseInt(heightMatch[1], 10);

        const aspectRatio = originalHeight / originalWidth;

        let updatedContent = content;
        updatedContent = updatedContent.replace(/width="\d+(?:px)?"/, 'width="100%"');
        updatedContent = updatedContent.replace(/height="\d+(?:px)?"/, '');

        if (updatedContent.includes('style="')) {
          updatedContent = updatedContent.replace(/style="([^"]*)"/, `style="$1; aspect-ratio: 1 / ${aspectRatio};"`);
        } else {
          updatedContent = updatedContent.replace(/(<iframe[^>]*)/, `$1 style="aspect-ratio: 1 / ${aspectRatio};"`);
        }
        item.updateUrl(updatedContent);
        setContent(updatedContent);
      }
    }
  }, [content]);

  return (
    <MemreFlexBox direction="column" gap={1} justify="center" align="center">
      {!content && !selectedType && (
        <MemreBoxSelection cardTypes={cardTypes} handleCardClick={handleCardClick} width="60px" />
      )}
      {content && (
        <MemreFlexBox direction="column" gap={2} sx={{ p: 1, flex: 1, width: '100%' }}>
          <div dangerouslySetInnerHTML={{ __html: content }} />
        </MemreFlexBox>
      )}
      {!content && selectedType && (
        <MemreFlexBox direction="column" gap={2} sx={{ p: 1, flex: 1 }}>
          <MemreText variant="h4">Embed {selectedType}</MemreText>
          <MemreFlexBox direction="row" gap={2} sx={{ p: 1, flex: 1 }}>
            {cardTypes.map((card) => {
              if (card.type === selectedType) {
                return (
                  <React.Fragment key={card.type}>
                    <img src={card.image} style={{ borderRadius: '4px', maxWidth: '40%' }} />
                    <MemreText sx={{ opacity: 0.7 }}>
                      <ol style={{ margin: 0, paddingLeft: '10px' }}>
                        {card.steps.map((step, index) => {
                          // If the first step includes a link
                          if (index === 0 && card.link) {
                            return (
                              <li key={index}>
                                <a href={card.link} target="_blank" rel="noreferrer">
                                  {card.linkText}
                                </a>{' '}
                                {step.replace(card.linkText, '')}
                              </li>
                            );
                          }
                          return <li key={index}>{step}</li>;
                        })}
                      </ol>
                    </MemreText>
                  </React.Fragment>
                );
              }
              return null;
            })}
          </MemreFlexBox>
          <MemreButton text="Paste Embed Code" onClick={handleEmbedClick} />
        </MemreFlexBox>
      )}
    </MemreFlexBox>
  );
}
